import type { NpcEncounter, NpcHullTier, NpcUnit } from "./types";
import {
  computeMissionDifficulty,
  type DifficultyBand,
  type MissionDifficultyResult,
  type PlayerCombatStats
} from "./missionDifficulty";

export interface CombatMission {
  encounter: NpcEncounter;
  difficulty: MissionDifficultyResult;
  rewardCr: number;
}

// Scores every roster encounter at the player's own hull tier through the
// Mission Difficulty Score engine (16.32). Reward reuses the encounter's own
// existing value directly (16.29) -- no separate pricing invented; the
// 7.5x/3.0x/1.0x Pirate<Faction<Alien reward multipliers are already baked
// into rewardCrEq (encounters.json v5).
export function scoreEncountersForTier(
  player: PlayerCombatStats,
  hullTier: NpcHullTier,
  encounters: NpcEncounter[],
  units: NpcUnit[]
): CombatMission[] {
  return encounters
    .filter((e) => e.hullTier === hullTier)
    .map((encounter) => ({
      encounter,
      difficulty: computeMissionDifficulty(player, encounter, units),
      rewardCr: encounter.rewardCrEq
    }));
}

// The locked 11-mission board shape (16.29): 2 Easy / 6 Challenge / 2 Very
// Hard / 1 Almost Impossible -- same curation shape as the Mining board
// (16.24).
const BOARD_BAND_TARGETS: Record<DifficultyBand, number> = {
  Easy: 2,
  Challenge: 6,
  "Very Hard": 2,
  "Almost Impossible": 1
};

const BOARD_BAND_ORDER: DifficultyBand[] = ["Easy", "Challenge", "Very Hard", "Almost Impossible"];

// Curates the 11-mission board out of scoreEncountersForTier()'s full pool.
// Within each band, missions are taken in the input array's own order (the
// roster's own ID order, if scored straight off encounters.json) -- 16.29
// doesn't lock a specific selection rule beyond hitting the target count.
// If a band doesn't have enough real candidates for a given ship (16.29
// itself hit this for some T3/T4 combos), this returns as many as actually
// exist rather than throwing or inventing filler encounters -- the roster
// is reused directly, never padded with invented stat blocks.
export function curateCombatMissionBoard(missions: CombatMission[]): CombatMission[] {
  return BOARD_BAND_ORDER.flatMap((band) =>
    missions.filter((m) => m.difficulty.band === band).slice(0, BOARD_BAND_TARGETS[band])
  );
}

export function generateCombatMissionBoard(
  player: PlayerCombatStats,
  hullTier: NpcHullTier,
  encounters: NpcEncounter[],
  units: NpcUnit[]
): CombatMission[] {
  return curateCombatMissionBoard(scoreEncountersForTier(player, hullTier, encounters, units));
}
